import { useCallback, useLayoutEffect, useRef, useState } from "react"

const isOverflowing = el => {
  if (!el) return false
  if (
    el.scrollWidth > el.clientWidth + 1 ||
    el.scrollHeight > el.clientHeight + 1
  ) {
    return true
  }
  // Grid/flex parents can constrain width while the child still reports equal sizes
  const parent = el.parentElement
  if (parent && el.scrollWidth > parent.clientWidth + 1) {
    return true
  }
  return false
}

/**
 * Returns a tooltip string only when the measured element is visually truncated.
 * Pass `content` for the tooltip text (falls back to textContent).
 */
const useTruncationTitle = content => {
  const ref = useRef(null)
  const [title, setTitle] = useState(undefined)

  const sync = useCallback(() => {
    const el = ref.current
    if (!el) {
      setTitle(undefined)
      return
    }

    const text =
      content != null && String(content).trim() !== ""
        ? String(content)
        : (el.textContent || "").trim()

    setTitle(text && isOverflowing(el) ? text : undefined)
  }, [content])

  useLayoutEffect(() => {
    sync()
    const el = ref.current
    if (!el) return undefined

    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(sync)
    }

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : null
    observer?.observe(el)
    if (el.parentElement) observer?.observe(el.parentElement)

    window.addEventListener("resize", schedule)

    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener("resize", schedule)
    }
  }, [sync])

  return { ref, title }
}

export default useTruncationTitle
